/**
 * CollectionsTab - Main collections management UI
 *
 * Displays list of collections and provides:
 * - Create new collection
 * - Edit existing collection schema
 * - View/manage collection items
 * - Import data from templates
 */

import { useState, useEffect } from 'react';
import type { CollectionSchema } from '@deep-agents/shared-types';
import { useCollections, type CollectionSchemaRecord } from '../../hooks/useCollections';
import { CollectionSchemaEditor } from './CollectionSchemaEditor';
import { CollectionItemsView } from './CollectionItemsView';

// ============================================================
// ICONS (inline SVG for simplicity)
// ============================================================

const PlusIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const FolderIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

const EditIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);

const TrashIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const ArrowLeftIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
  </svg>
);

// ============================================================
// TYPES
// ============================================================

interface CollectionsTabProps {
  appId: string;
}

type ViewMode = 'list' | 'items' | 'edit-schema';

// ============================================================
// COMPONENT
// ============================================================

export function CollectionsTab({ appId }: CollectionsTabProps) {
  const {
    schemas,
    isLoadingSchemas,
    schemasError,
    fetchSchemas,
    createSchema,
    updateSchema,
    deleteSchema,
    // AI schema assistant
    isGeneratingSchema,
    generateSchema,
  } = useCollections({ appId });

  // View state
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedCollection, setSelectedCollection] = useState<CollectionSchemaRecord | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  // Load schemas on mount
  useEffect(() => {
    fetchSchemas();
  }, [fetchSchemas]);

  // ─────────────────────────────────────────────────────────
  // Handlers
  // ─────────────────────────────────────────────────────────

  const handleCreateNew = () => {
    setSelectedCollection(null);
    setIsCreating(true);
    setViewMode('edit-schema');
  };

  const handleEditSchema = (collection: CollectionSchemaRecord) => {
    setSelectedCollection(collection);
    setIsCreating(false);
    setViewMode('edit-schema');
  };

  const handleViewItems = (collection: CollectionSchemaRecord) => {
    setSelectedCollection(collection);
    setViewMode('items');
  };

  const handleBack = () => {
    setViewMode('list');
    setSelectedCollection(null);
    setIsCreating(false);
  };

  const handleSaveSchema = async (name: string, schema: CollectionSchema) => {
    let success = false;

    if (isCreating) {
      const result = await createSchema(name, schema);
      success = result !== null;
    } else if (selectedCollection) {
      success = await updateSchema(selectedCollection.name, schema);
    }

    if (success) {
      handleBack();
    }
  };

  const handleDeleteCollection = async (name: string) => {
    if (!confirm(`Delete collection "${name}"? This will also delete all items in this collection.`)) {
      return;
    }

    const success = await deleteSchema(name);
    if (success && selectedCollection?.name === name) {
      handleBack();
    }
  };

  // ─────────────────────────────────────────────────────────
  // Render: Edit Schema View
  // ─────────────────────────────────────────────────────────

  if (viewMode === 'edit-schema') {
    return (
      <CollectionSchemaEditor
        schema={selectedCollection?.schema || null}
        isNew={isCreating}
        onSave={handleSaveSchema}
        onCancel={handleBack}
        onGenerateSchema={isCreating ? generateSchema : undefined}
        isGeneratingSchema={isGeneratingSchema}
      />
    );
  }

  // ─────────────────────────────────────────────────────────
  // Render: Items View
  // ─────────────────────────────────────────────────────────

  if (viewMode === 'items' && selectedCollection) {
    return (
      <CollectionItemsView
        appId={appId}
        collection={selectedCollection}
        onBack={handleBack}
        onEditSchema={() => handleEditSchema(selectedCollection)}
      />
    );
  }

  // ─────────────────────────────────────────────────────────
  // Render: Collections List
  // ─────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
            Collections
          </h3>
          <p className="text-xs text-gray-400 mt-1">
            Define content types for your app (exercises, recipes, etc.)
          </p>
        </div>
        <button
          onClick={handleCreateNew}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700 transition-colors"
        >
          <PlusIcon />
          New Collection
        </button>
      </div>

      {/* Error State */}
      {schemasError && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {schemasError}
        </div>
      )}

      {/* Loading State */}
      {isLoadingSchemas && (
        <div className="flex items-center justify-center py-8">
          <div className="w-6 h-6 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* Empty State */}
      {!isLoadingSchemas && schemas.length === 0 && (
        <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
          <FolderIcon />
          <p className="text-gray-500 mt-2 text-sm">No collections yet</p>
          <p className="text-gray-400 text-xs mt-1">
            Create a collection to define a content type for your app
          </p>
          <button
            onClick={handleCreateNew}
            className="mt-4 px-4 py-2 text-sm font-medium text-violet-600 bg-violet-50 rounded-lg hover:bg-violet-100 transition-colors"
          >
            Create your first collection
          </button>
        </div>
      )}

      {/* Collections List */}
      {!isLoadingSchemas && schemas.length > 0 && (
        <div className="space-y-2">
          {schemas.map((collection) => (
            <div
              key={collection.id}
              className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors group"
            >
              {/* Collection Icon & Info */}
              <div className="flex-shrink-0 w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                <FolderIcon />
              </div>

              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-medium text-gray-900 truncate">
                  {collection.schema.label || collection.name}
                </h4>
                <p className="text-xs text-gray-500 truncate">
                  {collection.schema.fields?.length || 0} fields
                  {collection.schema.description && ` · ${collection.schema.description}`}
                </p>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={() => handleEditSchema(collection)}
                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded transition-colors"
                  title="Edit schema"
                >
                  <EditIcon />
                </button>
                <button
                  onClick={() => handleDeleteCollection(collection.name)}
                  className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                  title="Delete collection"
                >
                  <TrashIcon />
                </button>
              </div>

              {/* View Items Button */}
              <button
                onClick={() => handleViewItems(collection)}
                className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Items
                <ChevronRightIcon />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
